// Site-wide navigation and integration data. Pages render from this, so adding
// a link, a solution page or an integration is a one-line change here.
import gmail from "@/assets/gmail.svg";
import outlook from "@/assets/outlook.svg";
import slack from "@/assets/slack.svg";
import teams from "@/assets/teams.svg";
import instagram from "@/assets/instagram.svg";
import messenger from "@/assets/messenger.svg";
import googleCalendar from "@/assets/google-calendar.png";
import { products, productPath } from "./products";

export interface NavLinkItem {
  label: string;
  href: string;
  description?: string;
  /** true = leaves the React app (login, dashboard, static pages). */
  external?: boolean;
}

export interface NavGroup {
  label: string;
  /** Present when the group is a plain link instead of a menu. */
  href?: string;
  columns?: { heading: string; links: NavLinkItem[] }[];
}

export interface Integration {
  key: string;
  name: string;
  logo: string;
  href?: string;
}

// Only tools a ClearMakes product really connects to.
export const integrations: Integration[] = [
  { key: "gmail", name: "Gmail", logo: gmail, href: "/gmail-integration" },
  { key: "outlook", name: "Outlook", logo: outlook, href: "/outlook-integration" },
  { key: "slack", name: "Slack", logo: slack, href: "/slack-integration" },
  { key: "teams", name: "Microsoft Teams", logo: teams, href: "/teams-integration" },
  { key: "instagram", name: "Instagram", logo: instagram, href: "/instagram-integration" },
  { key: "messenger", name: "Messenger", logo: messenger, href: "/facebook-integration" },
  { key: "google-calendar", name: "Google Calendar", logo: googleCalendar },
];

export const integrationByKey = (key: string) => integrations.find((i) => i.key === key);

export const REQUEST_DEMO_URL = "/request-demo";
/** ClearMakes Agents runs on its own subdomain. */
export const AGENTS_URL = "https://agents.clearmakes.com";
export const GET_STARTED_URL = "/get-started";
export const LOGIN_URL = "/login";

export const mainNav: NavGroup[] = [
  {
    label: "Products",
    columns: [
      {
        heading: "Products",
        links: [
          ...products.map((p) => ({ label: p.name, href: productPath(p), description: p.tagline })),
          { label: "All products", href: "/products", description: "Everything ClearMakes builds, in one place." },
        ],
      },
      {
        heading: "Inside Inbox",
        links: [
          { label: "Features", href: "/features" },
          { label: "Integrations", href: "/integrations" },
          { label: "Workflows", href: "/workflows" },
          { label: "Security", href: "/security-architecture" },
        ],
      },
      {
        heading: "Inside Agents",
        links: [
          { label: "Website chatbot", href: `${AGENTS_URL}/ai-chatbot-for-website` },
          { label: "Internal assistant", href: `${AGENTS_URL}/internal-ai-assistant` },
          { label: "MCP and apps", href: `${AGENTS_URL}/mcp-ai-agent` },
          { label: "Use cases", href: `${AGENTS_URL}/#team` },
        ],
      },
    ],
  },
  {
    label: "Solutions",
    columns: [
      {
        heading: "By team",
        links: [
          { label: "Founders", href: "/for-founders", description: "Run the whole company inbox without an assistant." },
          { label: "Remote teams", href: "/for-remote-teams", description: "Stay in sync across time zones and tools." },
          { label: "Freelancers", href: "/for-freelancers", description: "Every client conversation in one place." },
        ],
      },
      {
        heading: "By use case",
        links: [
          { label: "Customer conversations", href: "/customer-conversation-tracker" },
          { label: "E-commerce shared inbox", href: "/ecommerce-shared-inbox" },
          { label: "Small-business inbox", href: "/unified-inbox-small-business" },
          { label: "All solutions", href: "/solutions" },
        ],
      },
    ],
  },
  {
    label: "Resources",
    columns: [
      {
        heading: "Learn",
        links: [
          { label: "Documentation", href: "/documentation", description: "Set up and get the most from your workspace." },
          { label: "Resources", href: "/resources", description: "Guides, comparisons and how-tos." },
          { label: "Why ClearMakes", href: "/why-clearmakes" },
        ],
      },
      {
        heading: "Trust",
        links: [
          { label: "Security architecture", href: "/security-architecture" },
          { label: "Privacy", href: "/privacy" },
          { label: "Report a vulnerability", href: "/vulnerability" },
        ],
      },
    ],
  },
  {
    label: "Pricing",
    columns: [
      {
        heading: "Pricing",
        links: [
          { label: "ClearMakes Inbox", href: "/pricing", description: "Plans for one inbox or a whole team." },
          { label: "ClearMakes Agents", href: `${AGENTS_URL}/pricing`, description: "Free to start, paid plans from $12 a month." },
        ],
      },
    ],
  },
  {
    label: "Company",
    columns: [
      {
        heading: "Company",
        links: [
          { label: "About", href: "/company", description: "Who we are and what we are building." },
          { label: "Careers", href: "/careers" },
          { label: "Contact", href: "/contact" },
        ],
      },
    ],
  },
];

export const footerColumns: { heading: string; links: NavLinkItem[] }[] = [
  {
    heading: "Products",
    links: [
      ...products.map((p) => ({ label: p.name, href: productPath(p) })),
      { label: "All products", href: "/products" },
      { label: "Inbox pricing", href: "/pricing" },
      { label: "Agents pricing", href: `${AGENTS_URL}/pricing` },
      { label: "Integrations", href: "/integrations" },
    ],
  },
  {
    heading: "Solutions",
    links: [
      { label: "Founders", href: "/for-founders" },
      { label: "Remote teams", href: "/for-remote-teams" },
      { label: "Freelancers", href: "/for-freelancers" },
      { label: "All solutions", href: "/solutions" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Documentation", href: "/documentation" },
      { label: "Resources", href: "/resources" },
      { label: "Security", href: "/security-architecture" },
      { label: "Community guidelines", href: "/guidelines" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "/company" },
      { label: "Careers", href: "/careers" },
      { label: "Contact", href: "/contact" },
      { label: "Request a demo", href: REQUEST_DEMO_URL },
    ],
  },
];

export const legalLinks: NavLinkItem[] = [
  { label: "Terms", href: "/terms" },
  { label: "Privacy", href: "/privacy" },
  { label: "Refunds", href: "/refund" },
  { label: "Report a vulnerability", href: "/vulnerability" },
];

// The site is written in English. Other languages open a machine translation,
// and the menu says so rather than implying a native translation exists.
export const languages: { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
  { code: "hi", label: "हिन्दी" },
  { code: "ja", label: "日本語" },
];
